import React, { forwardRef } from 'react';
import { styled, IconButton } from '@mui/material';

import Icon from 'shell/components/Icon';
import Tooltip from './Tooltip';

const SIZES = ['xsmall', 'small', 'medium', 'large'];
const VARIANTS = ['circle', 'square'];

const CustomIconButton = styled(IconButton)(({ $disabled, $size, variant, theme }) => {
    const getSize = (variant) => {
        return variant === 'circle'
            ? {
                  xsmall: '1.5rem',
                  small: '1.75rem',
                  medium: '2.25rem',
                  large: '2.75rem',
              }[$size]
            : {
                  small: '1.5rem',
                  medium: '2rem',
                  large: '2.5rem',
              }[$size];
    };

    const styles = {
        circle: {
            background: theme.palette.ensightful.primary[500],
            color: theme.palette.ensightful.shades[0],
            '&:hover': {
                background: theme.palette.ensightful.primary[700],
            },
            '&:active': {
                background: theme.palette.ensightful.primary[800],
            },
            ...($disabled && {
                background: theme.palette.ensightful.neutral[200],
                color: theme.palette.ensightful.neutral[500],
                pointerEvents: 'none',
                '&:active': {
                    background: theme.palette.ensightful.neutral[200],
                },
            }),
        },
        square: {
            borderRadius: '4px',
            background: 'transparent',
            color: theme.palette.ensightful.neutral[700],
            '&:hover': {
                background: theme.palette.ensightful.neutral[100],
                color: theme.palette.ensightful.neutral[900],
            },
            '&:active': {
                background: theme.palette.ensightful.neutral[200],
                color: theme.palette.ensightful.neutral[900],
            },
            ...($disabled && {
                color: theme.palette.ensightful.neutral[400],
                pointerEvents: 'none',
                '&:hover': {
                    background: 'transparent',
                },
                '&:active': {
                    background: 'transparent',
                },
            }),
        },
    }[variant];

    return {
        width: getSize(variant),
        height: getSize(variant),
        transition: theme.transitions.default,
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
        ...styles,
    };
});

const CustomIcon = styled(Icon)(({ size }) => {
    const fontSize = {
        xsmall: '0.75rem',
        small: '0.75rem',
        medium: '1.125rem',
        large: '1.125rem',
    }[size];

    return {
        fontSize,
    };
});

export default forwardRef(({ disabled, kind, size, tooltip, variant, onClick, ...rest }, ref) => {
    if (tooltip && !tooltip.component)
        throw new Error(
            `Shell: Invalid tooltip in component IconButton. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
        );
    if (!SIZES.includes(size)) throw new Error(`Shell: Invalid size "${size}" in component IconButton.`);
    if (!VARIANTS.includes(variant)) throw new Error(`Shell: Invalid variant "${variant}" in component IconButton.`);
    if (size === 'xsmall' && variant === 'square') {
        throw new Error(
            `Shell: Invalid size "${size}" for variant "${variant}" in component IconButton. Please use one of "small", "medium" or "large" for size.`
        );
    }
    const IconButtonComponent = (
        <CustomIconButton
            ref={ref}
            aria-disabled={disabled}
            $disabled={disabled}
            $size={size}
            variant={variant}
            onClick={disabled ? undefined : onClick}
            {...rest}
        >
            <CustomIcon kind={kind} size={size} />
        </CustomIconButton>
    );

    return tooltip ? (
        <Tooltip title={tooltip.component} maxWidth={tooltip.maxWidth || '8.75rem'}>
            {IconButtonComponent}
        </Tooltip>
    ) : (
        IconButtonComponent
    );
});
